class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Your embroidery variant IDs from the PDP code
    this.embroideryVariantIds = ['46276449665236', '44865775010004', '44865775042772'];
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Setup embroidery handlers after the drawer is fully open
        setTimeout(() => {
          this.setupEmbroideryRemovalHandling();
        }, 100);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setupEmbroideryRemovalHandling() {
    console.log('Setting up embroidery removal handling...');
    
    // Handle cart-remove-button elements (your specific structure)
    const removeButtons = this.querySelectorAll('cart-remove-button button');
    removeButtons.forEach(button => {
      if (!button.dataset.embroideryHandlerAdded) {
        button.dataset.embroideryHandlerAdded = 'true';
        button.addEventListener('click', (e) => this.handleRemoveClick(e, button));
      }
    });

    // Handle quantity inputs
    const quantityInputs = this.querySelectorAll('input[name="updates[]"]');
    quantityInputs.forEach(input => {
      if (!input.dataset.embroideryHandlerAdded) {
        input.dataset.embroideryHandlerAdded = 'true';
        input.addEventListener('change', (e) => this.handleQuantityChange(e, input));
      }
    });

    console.log(`Found ${removeButtons.length} remove buttons and ${quantityInputs.length} quantity inputs`);
  }

  handleRemoveClick(event, button) {
    console.log('Remove button clicked');
    const cartItem = button.closest('tr.cart-item');
    const variantId = button.getAttribute('data-variant-id');
    
    console.log('Variant ID:', variantId);
    
    if (this.isEmbroideryVariant(variantId) || this.isEmbroideryItem(cartItem)) {
      event.preventDefault();
      event.stopPropagation();
      console.log('Embroidery item detected, clearing entire cart...');
      this.clearEntireCart();
    }
  }

  handleQuantityChange(event, input) {
    if (input.value === '0' || input.value === '') {
      const cartItem = input.closest('tr.cart-item');
      const variantId = input.getAttribute('data-quantity-variant-id');
      
      if (this.isEmbroideryVariant(variantId) || this.isEmbroideryItem(cartItem)) {
        console.log('Embroidery quantity set to 0, clearing entire cart...');
        this.clearEntireCart();
      }
    }
  }

  async clearEntireCart() {
    console.log('Starting cart clear...');
    
    const confirmClear = window.confirm('Removing embroidery will clear your entire cart. Continue?');
    if (!confirmClear) {
      // Reset any quantity inputs that were set to 0
      const quantityInputs = this.querySelectorAll('input[name="updates[]"]');
      quantityInputs.forEach(input => {
        if (input.value === '0' || input.value === '') {
          input.value = '1';
        }
      });
      return;
    }

    try {
      // Clear the entire cart
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        console.log('Cart cleared successfully, reloading page...');
        window.location.reload();
      } else {
        throw new Error('Failed to clear cart');
      }

    } catch (error) {
      console.error('Error clearing cart:', error);
      alert('Error clearing cart. Please try again.');
    }
  }

  isEmbroideryVariant(variantId) {
    return variantId && this.embroideryVariantIds.includes(variantId);
  }

  isEmbroideryItem(item) {
    if (!item) return false;

    // Check by variant ID from buttons/inputs
    const variantId = item.querySelector('button[data-variant-id]')?.getAttribute('data-variant-id') ||
                     item.querySelector('input[data-quantity-variant-id]')?.getAttribute('data-quantity-variant-id');
    
    if (this.isEmbroideryVariant(variantId)) {
      return true;
    }

    // Check by content/properties
    const itemContent = item.textContent || item.innerText || '';
    return itemContent.toLowerCase().includes('embroidery') || 
           itemContent.toLowerCase().includes('embroidered') ||
           itemContent.includes('Upsell Item');
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      
      // Setup embroidery handlers after content is rendered
      setTimeout(() => {
        this.setupEmbroideryRemovalHandling();
      }, 100);
      
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.embroideryVariantIds = ['46276449665236', '44865775010004', '44865775042772'];
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Override onChange to handle embroidery removal via quantity changes
  onChange(event) {
    console.log('CartDrawerItems onChange triggered');
    
    const isQuantityZero = event.target.name === 'updates[]' && (event.target.value === '0' || event.target.value === '');
    
    if (isQuantityZero) {
      const cartItem = event.target.closest('tr.cart-item');
      const variantId = event.target.getAttribute('data-quantity-variant-id');
      
      if (this.isEmbroideryVariant(variantId) || this.isEmbroideryItem(cartItem)) {
        console.log('Preventing normal onChange for embroidery item');
        event.preventDefault();
        
        // Let the CartDrawer handle this
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.handleQuantityChange(event, event.target);
        }
        return;
      }
    }
    
    // Call original onChange for non-embroidery items
    if (super.onChange) {
      super.onChange(event);
    }
  }

  isEmbroideryVariant(variantId) {
    return variantId && this.embroideryVariantIds.includes(variantId);
  }

  isEmbroideryItem(item) {
    if (!item) return false;

    const variantId = item.querySelector('button[data-variant-id]')?.getAttribute('data-variant-id') ||
                     item.querySelector('input[data-quantity-variant-id]')?.getAttribute('data-quantity-variant-id');
    
    if (this.isEmbroideryVariant(variantId)) {
      return true;
    }

    const itemContent = item.textContent || item.innerText || '';
    return itemContent.toLowerCase().includes('embroidery') || 
           itemContent.toLowerCase().includes('embroidered') ||
           itemContent.includes('Upsell Item');
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Additional setup for when cart drawer opens
document.addEventListener('DOMContentLoaded', function() {
  // Function to open the cart drawer
  function openCartDrawer() {
    console.log('Attempting to open cart drawer...');
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      console.log('Cart drawer found, opening...');
      
      // Try different methods to open the cart drawer
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        // Fallback: manually trigger cart drawer opening
        cartDrawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
      
      // Setup embroidery handlers after opening
      setTimeout(() => {
        if (cartDrawer.setupEmbroideryRemovalHandling) {
          cartDrawer.setupEmbroideryRemovalHandling();
        }
      }, 300);
    } else {
      console.warn('Cart drawer element not found - retrying...');
      // Retry after a short delay in case the element isn't ready yet
      setTimeout(openCartDrawer, 100);
    }
  }

  // Check URL for both addToCart and open_cart parameters
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('addToCart') === 'true' || urlParams.get('open_cart') === 'true') {
    console.log('Opening cart drawer from URL parameter:', window.location.search);
    
    // Small delay to ensure everything is loaded
    setTimeout(() => {
      openCartDrawer();
      // Clean up the URL (remove the query parameter)
      window.history.replaceState({}, document.title, window.location.pathname);
    }, 100);
  }

  // Re-setup handlers when cart drawer content changes
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' && mutation.target.id === 'CartDrawer-CartItems') {
        console.log('Cart items changed, re-setting up embroidery handlers');
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.setupEmbroideryRemovalHandling) {
          setTimeout(() => cartDrawer.setupEmbroideryRemovalHandling(), 100);
        }
      }
    });
  });

  const cartItemsContainer = document.getElementById('CartDrawer-CartItems');
  if (cartItemsContainer) {
    observer.observe(cartItemsContainer, { childList: true, subtree: true });
  }
});